import { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import PeptideCalculatorPro from "../components/PeptideCalculatorPro";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Calculator as CalcIcon, Syringe as SyringeIcon, Save, PlusCircle, Layers, HelpCircle, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";
import InfoTip from "../components/InfoTip";
import ProductTour from "../components/ProductTour";

const CALC_TOUR_KEY = "amino_calc_tour_seen_v1";
const CALC_TOUR_STEPS = [
  {
    placement: "center",
    title: "How to use the Calculator",
    body: "Turn milligrams + BAC water + a target dose into exact syringe units. Auto-fills from your library and can log the dose in one tap.",
  },
  {
    selector: '[data-testid="calc-peptide"]',
    placement: "bottom",
    title: "1. Peptide (optional)",
    body: "Pick from your library — dose amount + unit auto-fill from the peptide's recommended defaults. Skip if you want to enter everything manually.",
  },
  {
    selector: '[data-testid="calc-amount-mg"]',
    placement: "bottom",
    title: "2. Peptide (mg)",
    body: "Total mg in the vial — usually printed on the label (5 mg, 10 mg, etc.). Sets your starting concentration together with BAC water.",
  },
  {
    selector: '[data-testid="calc-bac-ml"]',
    placement: "bottom",
    title: "3. BAC water (mL)",
    body: "How much bacteriostatic water you added when reconstituting. This determines your mg/mL concentration.",
  },
  {
    selector: '[data-testid="calc-dose"]',
    placement: "bottom",
    title: "4. Desired dose",
    body: "The dose you want for this single shot. Pick mcg or mg next to it. Everything after this is derived.",
  },
  {
    selector: '[data-testid="calc-syringe-size"]',
    placement: "top",
    title: "5. Syringe size",
    body: "30u / 50u / 100u insulin syringe. If your draw would exceed the syringe, a warning appears — pick a bigger size or split the dose.",
  },
  {
    selector: '[data-testid="syringe-ruler"]',
    placement: "top",
    title: "6. Read the syringe",
    body: "Red pointer shows the exact draw position. Unit tick marks span cleanly across the bottom. mL equivalent is printed above.",
  },
  {
    selector: '[data-testid="calc-log-dose"]',
    placement: "top",
    title: "7. Log Dose (or Save Vial)",
    body: "Log Dose records the shot and auto-deducts from your inventory. Save Vial saves this reconstitution as a new inventory entry.",
  },
  {
    selector: '[data-testid="calc-split-toggle"]',
    placement: "top",
    title: "8. Multi-vial split (advanced)",
    body: "Toggle on to distribute one dose across multiple vials — perfect for finishing an almost-empty vial before opening a fresh one. Vials drain FIFO.",
  },
];

function Syringe({ maxUnits, drawUnits, doseAmount, doseUnit }) {
  const clamped = Math.max(0, Math.min(drawUnits, maxUnits));
  const pct = maxUnits > 0 ? (clamped / maxUnits) * 100 : 0;
  const mlValue = (clamped / 100).toFixed(2);
  // Major ticks every 10, minor every 1 (0..maxUnits).
  const majors = [];
  for (let u = 0; u <= maxUnits; u += 10) majors.push(u);
  const minors = [];
  for (let u = 0; u <= maxUnits; u += 1) if (u % 10 !== 0) minors.push(u);

  const doseLabel = doseAmount && doseUnit
    ? `${doseAmount} ${doseUnit}`
    : `${clamped.toFixed(1)} units`;

  return (
    <div className="w-full bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="syringe-ruler">
      {/* Instructional sentence */}
      <div className="text-sm text-slate-900 mb-4 leading-relaxed">
        To have a dose of <span className="font-semibold text-white">{doseLabel}</span>, pull the syringe to{" "}
        <span className="font-semibold text-[#EF4444]">{clamped.toFixed(1)} units</span>{" "}
        <span className="text-slate-600">({mlValue} mL)</span> on a{" "}
        <span className="text-slate-800">{maxUnits}-unit syringe</span>.
      </div>

      {/* Ruler with pointer */}
      <div className="relative pt-8 pb-2">
        {/* Red pointer + pill above the tick line */}
        <div
          className="absolute -top-0 flex flex-col items-center transition-all duration-200"
          style={{ left: `${pct}%`, transform: "translateX(-50%)" }}
          data-testid="syringe-pointer"
        >
          <div className="bg-[#EF4444] text-white text-[11px] font-bold font-mono-num px-2 py-0.5 rounded-full leading-tight mb-1 shadow-sm">
            {clamped.toFixed(1)}
          </div>
          <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-[#EF4444]" />
        </div>

        {/* Red filled bar from 0 → pointer */}
        <div className="relative h-6 border-l border-slate-300">
          <div
            className="absolute left-0 top-0 h-full bg-[#EF4444]/25 border-t border-b border-[#EF4444]/60 transition-all duration-200"
            style={{ width: `${pct}%` }}
          />
          {/* Minor ticks */}
          <div className="absolute inset-0 flex items-center">
            {minors.map((u) => (
              <span
                key={`min-${u}`}
                className="absolute top-1/2 -translate-y-1/2 h-2 w-px bg-zinc-600"
                style={{ left: `${(u / maxUnits) * 100}%` }}
              />
            ))}
            {majors.map((u) => (
              <span
                key={`maj-${u}`}
                className="absolute top-1/2 -translate-y-1/2 h-3 w-0.5 bg-zinc-400"
                style={{ left: `${(u / maxUnits) * 100}%` }}
              />
            ))}
          </div>
        </div>

        {/* Major-tick number labels — clean span across the bottom */}
        <div className="relative h-5 mt-2">
          {majors.map((u) => (
            <span
              key={`lbl-${u}`}
              className="absolute -translate-x-1/2 text-[11px] font-mono-num text-slate-800 tabular-nums tracking-wide"
              style={{ left: `${(u / maxUnits) * 100}%` }}
            >
              {u}
            </span>
          ))}
        </div>

        {/* Units caption */}
        <div className="text-center mt-1 text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono-num">
          units
        </div>
      </div>
    </div>
  );
}

export default function Calculator(props) {
  return props?.embedded ? <PeptideCalculatorPro {...props} /> : (
    <AppLayout>
      <PeptideCalculatorPro {...props} />
    </AppLayout>
  );
}

export function PeptideCalculatorInner({ embedded = false, preset = null, onSaved }) {
  const [peptides, setPeptides] = useState([]);
  const [vials, setVials] = useState([]);
  const [peptideName, setPeptideName] = useState("");
  const [amountMg, setAmountMg] = useState("10");
  const [bacMl, setBacMl] = useState("2");
  const [doseValue, setDoseValue] = useState("250");
  const [doseUnit, setDoseUnit] = useState("mcg");
  const [syringeSize, setSyringeSize] = useState("50");
  const [splitEnabled, setSplitEnabled] = useState(false);
  const [selectedVialIds, setSelectedVialIds] = useState([]);
  const [savingCatalog, setSavingCatalog] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/peptides"), api.get("/vials")])
      .then(([p, v]) => { setPeptides(p.data); setVials(v.data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  // Hydrate from a catalog preset (when a user clicks a saved calculation)
  useEffect(() => {
    if (!preset) return;
    const inp = preset.inputs || {};
    if (inp.peptide_name != null) setPeptideName(inp.peptide_name);
    if (inp.amount_mg != null) setAmountMg(String(inp.amount_mg));
    if (inp.bac_ml != null) setBacMl(String(inp.bac_ml));
    if (inp.dose_value != null) setDoseValue(String(inp.dose_value));
    if (inp.dose_unit) setDoseUnit(inp.dose_unit);
    if (inp.syringe_size != null) setSyringeSize(String(inp.syringe_size));
    if (inp.split_enabled != null) setSplitEnabled(!!inp.split_enabled);
  }, [preset]);

  const doseMcg = useMemo(() => {
    const v = parseFloat(doseValue);
    if (isNaN(v)) return 0;
    return doseUnit === "mg" ? v * 1000 : v;
  }, [doseValue, doseUnit]);

  const amt = parseFloat(amountMg) || 0;
  const bac = parseFloat(bacMl) || 0;
  const maxU = parseInt(syringeSize) || 50;

  const concentrationMcgMl = bac > 0 ? (amt * 1000) / bac : 0;
  const mlToDraw = concentrationMcgMl > 0 ? doseMcg / concentrationMcgMl : 0;
  const unitsToDraw = mlToDraw * 100;
  const totalDosesInVial = doseMcg > 0 ? (amt * 1000) / doseMcg : 0;

  const overflow = unitsToDraw > maxU;

  // Vials available for the selected peptide
  const eligibleVials = useMemo(() => {
    if (!peptideName) return [];
    return vials.filter((v) => v.peptide_name === peptideName && (v.remaining_mg ?? v.amount_mg) > 0)
      .sort((a, b) => {
        const ea = a.expiration_date ? new Date(a.expiration_date).getTime() : Infinity;
        const eb = b.expiration_date ? new Date(b.expiration_date).getTime() : Infinity;
        if (ea !== eb) return ea - eb;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
  }, [vials, peptideName]);

  // Auto-select all eligible vials when enabling split mode or when peptide changes
  useEffect(() => {
    if (!splitEnabled) return;
    setSelectedVialIds(eligibleVials.map((v) => v.id));
  }, [splitEnabled, eligibleVials]);

  // Split-dose distribution
  const splitPlan = useMemo(() => {
    if (!splitEnabled) return null;
    const totalDoseMg = doseMcg / 1000;
    if (totalDoseMg <= 0) return { totalMg: 0, allocations: [], remainingMg: 0, insufficient: false };
    const chosen = eligibleVials.filter((v) => selectedVialIds.includes(v.id));
    let remaining = totalDoseMg;
    const allocations = [];
    for (const v of chosen) {
      if (remaining <= 0) break;
      const available = Number(v.remaining_mg ?? v.amount_mg) || 0;
      const take = Math.min(available, remaining);
      if (take <= 0) continue;
      // Assume this vial uses the same reconstitution (bac water & mg reference) as the calculator inputs
      // Units to draw for this take: proportional
      const takeMcg = take * 1000;
      const mlForTake = concentrationMcgMl > 0 ? takeMcg / concentrationMcgMl : 0;
      const unitsForTake = mlForTake * 100;
      allocations.push({
        vial: v,
        takeMg: take,
        takeMcg,
        mlForTake,
        unitsForTake,
        newRemainingMg: available - take,
      });
      remaining -= take;
    }
    return {
      totalMg: totalDoseMg,
      allocations,
      remainingMg: Math.max(0, remaining),
      insufficient: remaining > 0.0000001,
    };
  }, [splitEnabled, doseMcg, eligibleVials, selectedVialIds, concentrationMcgMl]);

  const handlePeptideSelect = (name) => {
    setPeptideName(name);
    const p = peptides.find((x) => x.name === name);
    if (p) {
      if (p.default_dose != null) {
        setDoseValue(String(p.default_dose));
        if (p.default_unit && ["mcg", "mg"].includes(p.default_unit)) setDoseUnit(p.default_unit);
      }
    }
  };

  const logDose = async () => {
    if (!peptideName) return toast.error("Pick a peptide");
    if (doseMcg <= 0) return toast.error("Enter a valid dose");
    try {
      const res = await api.post("/shots", {
        peptide_name: peptideName,
        dose_amount: parseFloat(doseValue),
        dose_unit: doseUnit,
        injection_site: null,
        notes: `Reconstitution: ${amt}mg / ${bac}mL → ${unitsToDraw.toFixed(1)}u`,
        taken_at: new Date().toISOString(),
        interval_hours: 24,
      });
      const d = res.data || {};
      if (d.deducted_vial_id) {
        const rem = Number(d.deducted_vial_remaining_mg ?? 0);
        const total = Number(d.deducted_vial_amount_mg ?? 0);
        toast.success(`Dose logged • ${d.deducted_vial_peptide_name} → ${rem.toFixed(2)}/${total.toFixed(2)} mg left`);
      } else {
        toast.success("Dose logged");
      }
      // Refresh vials to reflect deduction
      api.get("/vials").then((r) => setVials(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveAsVial = async () => {
    if (!peptideName) return toast.error("Pick a peptide");
    try {
      await api.post("/vials", {
        peptide_name: peptideName,
        amount_mg: amt,
        bac_water_ml: bac,
        purity_pct: 100,
        quantity: 1,
      });
      toast.success("Vial saved to inventory");
      api.get("/vials").then((r) => setVials(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleVial = (id) => {
    setSelectedVialIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem(CALC_TOUR_KEY)) {
        const t = setTimeout(() => setTourOpen(true), 700);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, []);
  const closeTour = () => {
    setTourOpen(false);
    try { localStorage.setItem(CALC_TOUR_KEY, "1"); } catch { /* ignore */ }
  };

  const saveToCatalog = async () => {
    const conc = doseMcg > 0 && parseFloat(bacMl) > 0 ? (parseFloat(amountMg) * 1000) / parseFloat(bacMl) : 0;
    const drawMl = conc > 0 ? doseMcg / conc : 0;
    setSavingCatalog(true);
    try {
      await api.post("/calc-history", {
        calc_type: "peptide",
        label: peptideName || null,
        inputs: {
          peptide_name: peptideName || null,
          amount_mg: parseFloat(amountMg) || null,
          bac_ml: parseFloat(bacMl) || null,
          dose_value: parseFloat(doseValue) || null,
          dose_unit: doseUnit,
          syringe_size: parseInt(syringeSize) || 50,
          split_enabled: !!splitEnabled,
        },
        outputs: {
          concentration_mcg_per_ml: conc,
          draw_ml: drawMl,
          draw_units: drawMl * 100,
          dose_mcg: doseMcg,
        },
      });
      toast.success("Saved to catalog");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingCatalog(false);
    }
  };

  const headerBlock = (
    <div className="flex items-baseline justify-between mb-8">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <CalcIcon className="w-4 h-4 text-[#0D9488]" />
          <div className="font-mono-num text-[11px] text-[#0D9488] uppercase tracking-[0.3em]">// Precision Dosing</div>
        </div>
        <h1 className="font-display font-black uppercase tracking-tight text-5xl lg:text-6xl leading-none">Calculator</h1>
        <p className="text-slate-500 mt-3 text-sm">Input peptide + BAC water. Get exact units to draw on your insulin syringe.</p>
      </div>
      <button
        type="button"
        onClick={() => setTourOpen(true)}
        title="How to use the Calculator"
        aria-label="How to use the Calculator"
        data-testid="calc-tour-trigger"
        className="w-9 h-9 flex items-center justify-center border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-500 transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
    </div>
  );

  const saveCatalogBtn = (
    <div className="flex justify-end mb-3">
      <Button
        type="button"
        variant="outline"
        onClick={saveToCatalog}
        disabled={savingCatalog}
        className="border-slate-200 bg-transparent hover:bg-slate-50 hover:text-[#0D9488] hover:border-[#0D9488] rounded-none text-slate-800 h-9 px-3 uppercase tracking-widest text-[10px] font-mono-num"
        data-testid="calc-save-catalog"
      >
        <BookmarkPlus className="w-3.5 h-3.5 mr-1.5" /> {savingCatalog ? "Saving…" : "Save to catalog"}
      </Button>
    </div>
  );

  return (
    <>
      {!embedded && headerBlock}
      {embedded && saveCatalogBtn}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-2 bg-white border border-slate-200 rounded-none p-6" data-testid="calc-inputs">
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-5">// Inputs</div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Peptide (optional)</Label>
              <Select value={peptideName} onValueChange={handlePeptideSelect}>
                <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="calc-peptide">
                  <SelectValue placeholder="Select from library" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none max-h-[300px]">
                  {peptides.map((p) => (
                    <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1.5">
                  Peptide (mg)
                  <InfoTip text="Total milligrams of lyophilized (dry) peptide in your vial — usually printed on the label (e.g. 5 mg, 10 mg)." />
                </Label>
                <Input type="number" step="any" value={amountMg} onChange={(e) => setAmountMg(e.target.value)}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num text-lg" data-testid="calc-amount-mg" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num flex items-center gap-1.5">
                  BAC Water (mL)
                  <InfoTip text="Bacteriostatic water — sterile water with 0.9% benzyl alcohol used to reconstitute peptides. The volume you add determines your final concentration (mg/mL)." />
                </Label>
                <Input type="number" step="any" value={bacMl} onChange={(e) => setBacMl(e.target.value)}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num text-lg" data-testid="calc-bac-ml" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Desired Dose</Label>
                <Input type="number" step="any" value={doseValue} onChange={(e) => setDoseValue(e.target.value)}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num text-lg" data-testid="calc-dose" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Unit</Label>
                <Select value={doseUnit} onValueChange={setDoseUnit}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="calc-dose-unit"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none">
                    <SelectItem value="mcg">mcg</SelectItem>
                    <SelectItem value="mg">mg</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Syringe Size (units)</Label>
              <Select value={syringeSize} onValueChange={setSyringeSize}>
                <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="calc-syringe-size"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none">
                  <SelectItem value="30">30u (0.3 mL)</SelectItem>
                  <SelectItem value="50">50u (0.5 mL)</SelectItem>
                  <SelectItem value="100">100u (1.0 mL)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={logDose}
                className="flex-1 h-11 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest font-semibold"
                data-testid="calc-log-dose">
                <PlusCircle className="w-4 h-4 mr-2" /> Log Dose
              </Button>
              <Button onClick={saveAsVial} variant="outline"
                className="h-11 border-slate-200 bg-transparent hover:bg-slate-50 hover:text-white rounded-none text-slate-800"
                data-testid="calc-save-vial">
                <Save className="w-4 h-4 mr-2" /> Save Vial
              </Button>
            </div>
          </div>
        </Card>

        <Card className="lg:col-span-3 bg-white border border-slate-200 rounded-none p-6 lg:p-8" data-testid="calc-results">
          <div className="flex items-center gap-2 mb-4">
            <SyringeIcon className="w-4 h-4 text-[#0D9488]" />
            <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// Draw Visualization</div>
          </div>

          {overflow && (
            <div className="mb-4 border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] text-xs px-3 py-2 font-mono-num uppercase tracking-widest" data-testid="calc-overflow-warn">
              Dose exceeds selected syringe — increase syringe size or reduce dose
            </div>
          )}

          <Syringe maxUnits={maxU} drawUnits={unitsToDraw} />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-slate-200">
            <ResultTile label="Draw" value={unitsToDraw.toFixed(1)} unit="units" highlight testid="calc-units" />
            <ResultTile label="Volume" value={mlToDraw.toFixed(3)} unit="mL" testid="calc-ml" />
            <ResultTile label="Concentration" value={(concentrationMcgMl / 1000).toFixed(2)} unit="mg/mL" testid="calc-conc" />
            <ResultTile label="Doses/Vial" value={totalDosesInVial.toFixed(1)} unit="ea" testid="calc-doses" />
          </div>

          <div className="mt-6 pt-6 border-t border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <FormulaLine label="Concentration" value={`${amt || 0}mg × 1000 ÷ ${bac || 0}mL = ${(concentrationMcgMl || 0).toFixed(0)} mcg/mL`} />
            <FormulaLine label="Volume needed" value={`${doseMcg || 0}mcg ÷ ${(concentrationMcgMl || 0).toFixed(0)} mcg/mL = ${(mlToDraw || 0).toFixed(3)} mL`} />
            <FormulaLine label="On U-100 syringe" value={`${(mlToDraw || 0).toFixed(3)}mL × 100 = ${(unitsToDraw || 0).toFixed(1)} u`} />
          </div>
        </Card>
      </div>

      {/* Multi-Vial Split Section */}
      <Card className="mt-6 bg-white border border-slate-200 rounded-none p-6 lg:p-8" data-testid="calc-split-card">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Layers className="w-4 h-4 text-[#0D9488]" />
              <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// Multi-Vial Split Dosing</div>
            </div>
            <div className="font-display font-black text-2xl uppercase tracking-tight">Distribute across vials</div>
            <p className="text-slate-500 mt-1 text-xs max-w-xl">
              Pull the same dose from multiple vials in inventory (e.g., finish an almost-empty vial + top up from a full one). Vials are drained in FIFO order.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Enable split</Label>
            <Switch checked={splitEnabled} onCheckedChange={setSplitEnabled}
              className="data-[state=checked]:bg-[#0D9488]" data-testid="calc-split-toggle" />
          </div>
        </div>

        {splitEnabled && !peptideName && (
          <div className="text-slate-500 text-sm font-mono-num uppercase tracking-widest text-xs border border-slate-200 p-4">
            Pick a peptide above to see eligible vials.
          </div>
        )}

        {splitEnabled && peptideName && eligibleVials.length === 0 && (
          <div className="text-slate-500 text-sm font-mono-num uppercase tracking-widest text-xs border border-slate-200 p-4" data-testid="calc-split-empty">
            No vials in inventory match "{peptideName}". Save one from this calculator or in Inventory.
          </div>
        )}

        {splitEnabled && peptideName && eligibleVials.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="calc-split-grid">
            {/* Left: vial toggles */}
            <div>
              <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Available Vials</div>
              <div className="space-y-2">
                {eligibleVials.map((v) => {
                  const rem = Number(v.remaining_mg ?? v.amount_mg) || 0;
                  const total = Number(v.amount_mg) || 0;
                  const pct = total > 0 ? Math.max(0, Math.min(100, (rem / total) * 100)) : 0;
                  const selected = selectedVialIds.includes(v.id);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => toggleVial(v.id)}
                      className={`w-full text-left border p-3 transition-colors ${selected ? "border-[#0D9488] bg-[#0D9488]/5" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid={`split-vial-toggle-${v.id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="font-mono-num text-slate-900 truncate">
                          {v.peptide_name}
                        </div>
                        <div className={`font-mono-num text-[10px] uppercase tracking-widest ${selected ? "text-[#0D9488]" : "text-slate-500"}`}>
                          {selected ? "// SELECTED" : "// tap to select"}
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="font-mono-num text-xs text-slate-600">
                          {rem.toFixed(2)} / {total.toFixed(2)} mg
                        </div>
                        {v.expiration_date && (
                          <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest">
                            exp {new Date(v.expiration_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" })}
                          </div>
                        )}
                      </div>
                      <div className="h-1 bg-slate-50">
                        <div
                          className="h-full"
                          style={{ width: `${pct}%`, background: pct < 25 ? "#0D9488" : "#22C55E" }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: split plan */}
            <div>
              <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Draw Plan</div>
              {(!splitPlan || splitPlan.allocations.length === 0) ? (
                <div className="border border-slate-200 p-4 text-xs font-mono-num text-slate-500 uppercase tracking-widest">
                  Select at least one vial to see the split plan.
                </div>
              ) : (
                <div className="space-y-2">
                  {splitPlan.allocations.map((a, i) => (
                    <div key={a.vial.id} className="border border-slate-200 p-3" data-testid={`split-allocation-${i}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest">Step {i + 1}</div>
                        <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest">
                          Vial {a.vial.id.slice(-4)}
                        </div>
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-mono-num text-slate-900 text-lg">
                          Draw <span className="text-[#0D9488] font-black">{a.unitsForTake.toFixed(1)} u</span>
                        </div>
                        <div className="font-mono-num text-xs text-slate-500">
                          = {a.takeMg.toFixed(3)} mg / {a.mlForTake.toFixed(3)} mL
                        </div>
                      </div>
                      <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest mt-1">
                        Vial leaves {a.newRemainingMg.toFixed(2)} mg
                      </div>
                    </div>
                  ))}
                  <div className="border-t border-slate-200 mt-3 pt-3 flex items-baseline justify-between gap-2">
                    <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest">Total delivered</div>
                    <div className="font-mono-num font-black text-lg text-[#0D9488]" data-testid="split-total-delivered">
                      {(splitPlan.totalMg - splitPlan.remainingMg).toFixed(3)} / {splitPlan.totalMg.toFixed(3)} mg
                    </div>
                  </div>
                  {splitPlan.insufficient && (
                    <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] text-xs px-3 py-2 font-mono-num uppercase tracking-widest" data-testid="split-insufficient-warn">
                      Short {splitPlan.remainingMg.toFixed(3)} mg — enable more vials or lower dose.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
      <ProductTour steps={CALC_TOUR_STEPS} open={tourOpen} onClose={closeTour} />
    </>
  );
}

function ResultTile({ label, value, unit, highlight, testid }) {
  return (
    <div className={`p-4 border ${highlight ? "border-[#0D9488]/40 bg-[#0D9488]/5" : "border-slate-200"}`} data-testid={testid}>
      <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">{label}</div>
      <div className={`font-display font-black text-4xl leading-none ${highlight ? "text-[#0D9488]" : "text-white"}`}>{value}</div>
      <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mt-2">{unit}</div>
    </div>
  );
}

function FormulaLine({ label, value }) {
  return (
    <div>
      <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</div>
      <div className="font-mono-num text-slate-800">{value}</div>
    </div>
  );
}
